/**
 * Escolha de unidade no cabecalho publico.
 *
 * O titulo do cabecalho ja diz em qual unidade a pessoa esta — repetir esse
 * nome num controle ao lado e redundancia. Entao aqui nao ha nome nenhum: so o
 * convite, dizendo quantas outras existem.
 *
 * Tres tentativas anteriores colaram uma superficie branca no cabecalho
 * (pastilhas numa pista, cartoes, abas com lombada) e todas brigaram com ele:
 * o cabecalho e tipografia e icone fino sobre cor, e o conteudo abaixo e que e
 * feito de cards. Um controle de superficie ali sempre leu como remendo. Este
 * usa a linguagem do proprio cabecalho — pastilha de contorno, cor herdada do
 * texto — e a lista vai para uma folha subindo de baixo, o mesmo padrao dos
 * detalhes de servico.
 *
 * Trocar de unidade troca servicos, profissionais e horarios: e mudanca de
 * contexto, e por isso merece o foco protegido da folha.
 */

import React from "react";
import { FiCheck, FiChevronDown, FiHome, FiMapPin, FiX } from "react-icons/fi";
import type { PublicBranch } from "../../../services/public";

interface BranchSwitcherProps {
  branches: PublicBranch[];
  currentSlug: string;
  labelColor: string;
  formatName: (value: string) => string;
  getLocation: (branch: PublicBranch) => string;
  onBranchChange?: (slug: string) => void;
}

export function BranchSwitcher({
  branches,
  currentSlug,
  labelColor,
  formatName,
  getLocation,
  onBranchChange,
}: BranchSwitcherProps) {
  const [aberta, setAberta] = React.useState(false);
  const outras = Math.max(0, branches.length - 1);

  React.useEffect(() => {
    if (!aberta) return;

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") setAberta(false);
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [aberta]);

  /**
   * O nome basta na lista. A cidade entra quando duas unidades dividem o mesmo
   * nome — sem ela apareceriam identicas.
   */
  const nomeRepetido = React.useMemo(() => {
    const contagem = new Map<string, number>();
    for (const branch of branches) {
      const nome = formatName(branch.name || "");
      contagem.set(nome, (contagem.get(nome) || 0) + 1);
    }
    return contagem;
  }, [branches, formatName]);

  return (
    <div className="min-w-0" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        onClick={() => setAberta(true)}
        data-testid="branch-switcher-trigger"
        aria-haspopup="dialog"
        aria-expanded={aberta}
        className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-current/30 px-3.5 text-sm font-semibold transition hover:border-current/60 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        style={{ color: labelColor }}
      >
        <FiHome className="h-4 w-4 opacity-80" aria-hidden="true" />
        {outras === 1 ? "Outra unidade" : `Outras ${outras} unidades`}
        <FiChevronDown className="h-4 w-4 opacity-80" aria-hidden="true" />
      </button>

      {aberta && (
        <div
          className="fixed inset-0 z-[70] flex animate-backdrop-in items-end justify-center bg-slate-950/70 backdrop-blur-sm motion-reduce:animate-none sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Escolha a unidade"
          data-testid="branch-switcher-sheet"
          onClick={() => setAberta(false)}
        >
          <div
            className="flex max-h-[80dvh] w-full animate-sheet-up flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl will-change-transform motion-reduce:animate-none sm:max-w-md sm:animate-dialog-in sm:rounded-3xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 pb-1 pt-5">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-[#233055]">Escolha a unidade</h2>
                <p className="mt-0.5 text-sm text-slate-600">
                  Os serviços e horários mudam conforme a unidade.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAberta(false)}
                aria-label="Fechar"
                data-testid="branch-sheet-close"
                className="-mr-1 -mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
              >
                <FiX className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="overflow-y-auto px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2">
              {branches.map((branch, index) => {
                const slug = String(branch.slug || "").trim();
                const atual = slug === currentSlug;
                const nome = formatName(branch.name || `Unidade ${index + 1}`);
                const local = getLocation(branch);
                const mostrarLocal = Boolean(local) && (nomeRepetido.get(nome) || 0) > 1;

                return (
                  <button
                    key={`${branch.id}-${branch.slug}`}
                    type="button"
                    aria-current={atual ? "true" : undefined}
                    data-testid={`branch-option-${slug}`}
                    onClick={() => {
                      setAberta(false);
                      if (!slug || atual) return;
                      onBranchChange?.(slug);
                    }}
                    className={`flex min-h-[56px] w-full items-center gap-3 rounded-2xl px-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 ${
                      atual ? "bg-primary-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <span
                      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        atual
                          ? "bg-primary-600 text-white"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {atual ? (
                        <FiCheck className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <FiMapPin className="h-4 w-4" aria-hidden="true" />
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-bold text-[#233055]">
                        {nome}
                      </span>
                      <span className="block truncate text-xs font-medium text-slate-500">
                        {mostrarLocal
                          ? local
                          : atual
                            ? "Você está aqui"
                            : "Toque para ver"}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
